import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toggleFavourite } from './recipes.service'
import { showToast } from '@/shared/utils/toast'
import type { Recipe, RecipeDetail } from './types'

export function useToggleFavourite() {
  const queryClient = useQueryClient()
  const { t } = useTranslation('recipes')

  return useMutation({
    mutationFn: ({ recipeId, isFavourite }: { recipeId: string; isFavourite: boolean }) =>
      toggleFavourite(recipeId, isFavourite),
    onMutate: async ({ recipeId, isFavourite }) => {
      await queryClient.cancelQueries({ queryKey: ['recipes', 'list'] })
      await queryClient.cancelQueries({ queryKey: ['recipes', 'detail', recipeId] })

      const prevList = queryClient.getQueryData<Recipe[]>(['recipes', 'list'])
      const prevDetail = queryClient.getQueryData<RecipeDetail>(['recipes', 'detail', recipeId])

      queryClient.setQueryData<Recipe[]>(['recipes', 'list'], (old) =>
        old?.map((r) => (r.id === recipeId ? { ...r, isFavourite } : r)) ?? [],
      )
      queryClient.setQueryData<RecipeDetail>(['recipes', 'detail', recipeId], (old) =>
        old ? { ...old, isFavourite } : old,
      )

      return { prevList, prevDetail, recipeId }
    },
    onSuccess: (_data, { isFavourite }) => {
      showToast(isFavourite ? t('favourites.added') : t('favourites.removed'))
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData(['recipes', 'list'], ctx?.prevList)
      queryClient.setQueryData(['recipes', 'detail', ctx?.recipeId], ctx?.prevDetail)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['recipes', 'list'] })
    },
  })
}
